// src/components/JarvislySimpleComponents/SimpleLoadingArea.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useEffect, useRef } from 'react';
import { Spin } from 'antd';
import { printGlobalStateToConsole } from 'configs/ConfigIndex';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import {
  StyledContentCenter,
  StyledCover,
  StyledTitleCenter,
} from 'layouts/SharedComponents/StyledComponents';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DefaultIcon = null; // <LoadingOutlined style={{ fontSize: 35 }} spin />;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleLoadingArea = ({
  name,
  visible,
  icon,
  title,
  translucid,
  offsetTop = 0,
  centeredWithoutOffset = true,
  showProgress,
  borderRadius = 0,
  zIndex = 500,
  style = {},
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedLoadingAreas = useZustand('boot', 'loadingAreas', true, true);
  const storedLoadingApp = useZustand('boot', 'loadingApp', true, true);
  const storedLoadingModule = useZustand('boot', 'loadingModule', true, true);
  const storedLoadingPage = useZustand('boot', 'loadingPage', true, true);
  const currentTheme = useZustand('theme', 'currentTheme');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const rootRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const el = rootRef.current;
    const parent = el?.parentElement;
    if (!parent) return;
    const computed = window.getComputedStyle(parent);
    if (computed.position === 'static') parent.style.position = 'relative';
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const sDefault = storedLoadingAreas?.__default || {};
  const sAll = storedLoadingAreas?.__all || {};
  const sNamed = name ? storedLoadingAreas?.[name] || {} : {};

  // precedence: named < __all < props (or __default < __all < props without name)
  const base = name ? { ...sNamed } : { ...sDefault };
  const merged = { ...base, ...sAll };

  const showBase = typeof visible === 'boolean' ? visible : !!merged.visible;
  const show =
    showBase &&
    !storedLoadingApp?.visible &&
    !storedLoadingModule?.visible &&
    !storedLoadingPage?.visible;

  const finalTranslucid =
    typeof translucid === 'boolean' ? translucid : (merged.translucid ?? true);

  const finalShowProgress =
    typeof showProgress === 'boolean' ? showProgress : (merged.showProgress ?? true);

  const iconNode =
    icon === false ? null : (icon ?? (merged.icon === false ? null : (merged.icon ?? DefaultIcon)));

  const finalTitle = title ?? merged.title;
  const tip = finalTitle ? translateX(finalTitle) : null;

  const shouldShowIcon = finalShowProgress;
  const shouldShowTitle = finalShowProgress && tip;

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!show) return null;

  return (
    <StyledCover
      ref={rootRef}
      translucid={finalTranslucid}
      currentTheme={currentTheme}
      offsetTop={offsetTop}
      onDoubleClick={printGlobalStateToConsole}
      customZIndex={zIndex}
      customPosition="absolute"
      customBorderRadius={borderRadius}
      customTop={`-${offsetTop}px`}
      style={{
        left: 0,
        right: 0,
        bottom: 0,
        ...(style || {}),
      }}
    >
      <StyledContentCenter>
        {shouldShowIcon ? (
          iconNode ? (
            <Spin
              style={centeredWithoutOffset ? { marginTop: offsetTop || 0 } : {}}
              indicator={iconNode}
            />
          ) : (
            <Spin style={centeredWithoutOffset ? { marginTop: offsetTop || 0 } : {}} />
          )
        ) : null}
        {shouldShowTitle ? (
          <StyledTitleCenter currentTheme={currentTheme}>{tip}</StyledTitleCenter>
        ) : null}
      </StyledContentCenter>
    </StyledCover>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleLoadingArea;

// *******************************************************************************
// HELPERS
// *******************************************************************************
